// Klinik Yönetimi › "Hizmetler ve Paketler": düz liste (design-principles §3) +
// sağdan editör drawer'ları. Hizmet ve paket şablonu aynı sekmede, üstteki
// çiplerle seçilir. Yazma `services:manage` ister (sekme de buna bağlı).
import useTerms from "../../../hooks/useTerms";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material";
import {
  MedicalServicesOutlined as ServiceIcon,
  Inventory2Outlined as PackageIcon,
} from "@mui/icons-material";
import { ActionButton, ChoiceChipGroup, FormCheckbox } from "../../atoms";
import DataTableCard from "../DataTableCard";
import {
  ClickableRow,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  SkeletonRows,
  StatusPill,
  TextCell,
} from "../ClinicRecordList";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS } from "../../../styles/uiConstants";
import { formatMoney } from "../../../utils/money";
import {
  useListBillingServicesMutation,
  useCreateBillingServiceMutation,
  useUpdateBillingServiceMutation,
  useListPackageTemplatesMutation,
  useCreatePackageTemplateMutation,
  useUpdatePackageTemplateMutation,
} from "../../../store/billingApi";
import ServiceEditorDrawer from "./ServiceEditorDrawer";
import PackageTemplateEditorDrawer from "./PackageTemplateEditorDrawer";

const PAGE = 25;

export default function ServiceCatalogPanel({ token, canManage = true }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const [kind, setKind] = useState("services");
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(0);
  const [services, setServices] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // { kind, item|null }

  const [listServices] = useListBillingServicesMutation();
  const [listTemplates] = useListPackageTemplatesMutation();
  const [createService, { isLoading: creatingS }] = useCreateBillingServiceMutation();
  const [updateService, { isLoading: updatingS }] = useUpdateBillingServiceMutation();
  const [createTemplate, { isLoading: creatingT }] = useCreatePackageTemplateMutation();
  const [updateTemplate, { isLoading: updatingT }] = useUpdatePackageTemplateMutation();

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const [s, t] = await Promise.all([
        listServices({ token, includeInactive: true }).unwrap(),
        listTemplates({ token, includeInactive: true }).unwrap(),
      ]);
      setServices(s?.data?.services || []);
      setTemplates(t?.data?.templates || []);
    } catch (e) {
      setServices((v) => v || []);
      setTemplates((v) => v || []);
      showError(e?.data?.message || "Hizmetler yüklenemedi");
    }
  }, [token, listServices, listTemplates, showError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(0), [kind, showInactive]);

  const loading = services === null || templates === null;
  const rows = useMemo(() => {
    const src = (kind === "services" ? services : templates) || [];
    return showInactive ? src : src.filter((x) => x.isActive !== false);
  }, [kind, services, templates, showInactive]);
  const activeCount = (list) => (list || []).filter((x) => x.isActive !== false).length;
  const pageRows = rows.slice(page * PAGE, page * PAGE + PAGE);

  const save = async (payload) => {
    const isService = editing?.kind === "services";
    const isNew = !editing?.item;
    try {
      if (isService) {
        await (isNew ? createService : updateService)({ token, ...payload }).unwrap();
      } else {
        await (isNew ? createTemplate : updateTemplate)({ token, ...payload }).unwrap();
      }
      showSuccess(isNew ? (isService ? "Hizmet eklendi" : "Paket eklendi") : "Değişiklikler kaydedildi");
      setEditing(null);
      load();
    } catch (e) {
      showError(e?.data?.message || "Kaydedilemedi");
    }
  };

  const cols = kind === "services" ? 4 : 5;
  const isEmpty = !loading && rows.length === 0;
  const emptyNew = (
    <Box
      component="button"
      type="button"
      onClick={() => setEditing({ kind, item: null })}
      sx={{ border: 0, bgcolor: "transparent", p: 0, cursor: "pointer", color: palette.primary, font: "inherit" }}
    >
      {kind === "services" ? "yeni hizmet ekleyin" : "yeni paket ekleyin"}
    </Box>
  );

  return (
    <Box sx={{ height: "100%", minHeight: 480, display: "flex", flexDirection: "column", bgcolor: COLORS.white }}>
      {/* Araç satırı */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          px: { xs: 2, md: 4 },
          py: 1.5,
        }}
      >
        <ChoiceChipGroup
          ariaLabel="Liste"
          value={kind}
          onChange={(v) => v && setKind(v)}
          options={[
            { value: "services", label: `Hizmetler · ${activeCount(services)}`, icon: <ServiceIcon /> },
            { value: "packages", label: `Paketler · ${activeCount(templates)}`, icon: <PackageIcon /> },
          ]}
        />
        <FormCheckbox
          palette={palette}
          label="Kullanım dışı olanları da göster"
          checked={showInactive}
          onChange={setShowInactive}
        />
        <Box sx={{ flex: 1 }} />
        {canManage ? (
          <ActionButton
            variant="add"
            palette={palette}
            label={kind === "services" ? "Yeni hizmet" : "Yeni paket"}
            onClick={() => setEditing({ kind, item: null })}
            disabled={kind === "packages" && activeCount(services) === 0}
          />
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <DataTableCard
      loading={Boolean(loading)}
          variant="flat"
          empty={isEmpty}
          emptyIcon={kind === "services" ? <ServiceIcon /> : <PackageIcon />}
          emptyTitle={kind === "services" ? "Henüz hizmet yok" : "Henüz paket yok"}
          emptyMessage={
            kind === "services" ? (
              <>Randevuda seçilecek hizmetleri burada tanımlayın veya {emptyNew}.</>
            ) : activeCount(services) === 0 ? (
              "Paket tanımlamak için önce bir hizmet ekleyin."
            ) : (
              <>Seans paketleri {terms.patientDatLower} satılır ve randevu tamamlandıkça hak düşer. Başlamak için {emptyNew}.</>
            )
          }
          page={page}
          rowsPerPage={PAGE}
          rowsPerPageOptions={[PAGE]}
          totalCount={rows.length}
          onPageChange={(_, p) => setPage(p)}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              {kind === "services" ? (
                <TableRow>
                  <PlainHead sx={{ ...EDGE_L, width: "45%" }}>Hizmet</PlainHead>
                  <PlainHead sx={{ width: "15%" }}>Süre</PlainHead>
                  <PlainHead sx={{ width: "20%" }}>Fiyat</PlainHead>
                  <PlainHead sx={{ ...EDGE_R, width: "20%" }}>Durum</PlainHead>
                </TableRow>
              ) : (
                <TableRow>
                  <PlainHead sx={{ ...EDGE_L, width: "30%" }}>Paket</PlainHead>
                  <PlainHead sx={{ width: "25%" }}>Hizmet</PlainHead>
                  <PlainHead sx={{ width: "15%" }}>Seans · Süre</PlainHead>
                  <PlainHead sx={{ width: "15%" }}>Fiyat</PlainHead>
                  <PlainHead sx={{ ...EDGE_R, width: "15%" }}>Durum</PlainHead>
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {loading ? (
                <SkeletonRows rows={6} cols={cols} />
              ) : isEmpty ? (
                <FillerRow cols={cols} />
              ) : (
                pageRows.map((row) => {
                  const status = (
                    <StatusPill
                      label={row.isActive !== false ? (kind === "services" ? "Kullanımda" : "Satışta") : "Kullanım dışı"}
                      tone={row.isActive !== false ? "success" : "neutral"}
                    />
                  );
                  const open = () => canManage && setEditing({ kind, item: row });
                  return kind === "services" ? (
                    <ClickableRow key={row._id} palette={palette} onClick={open}>
                      <TextCell sx={EDGE_L} main={row.name} sub={row.note || null} tooltip={row.note} />
                      <TextCell main={`${row.durationMin} dk`} />
                      <TextCell main={formatMoney(row.price)} />
                      <TextCell sx={EDGE_R} main={status} />
                    </ClickableRow>
                  ) : (
                    <ClickableRow key={row._id} palette={palette} onClick={open}>
                      <TextCell sx={EDGE_L} main={row.name} />
                      <TextCell main={row.service?.name || "—"} />
                      <TextCell main={`${row.sessionCount} seans`} sub={`${row.validityDays} gün geçerli`} />
                      <TextCell
                        main={formatMoney(row.price)}
                        sub={`Seans başı ${formatMoney(Math.round(row.price / Math.max(1, row.sessionCount)))}`}
                      />
                      <TextCell sx={EDGE_R} main={status} />
                    </ClickableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </Box>

      {!canManage ? (
        <Typography sx={{ px: { xs: 2, md: 4 }, py: 1, color: COLORS.slate500, fontSize: "0.75rem" }}>
          Yalnızca görüntüleme — düzenlemek için "Hizmet ve paket tanımlama" yetkisi gerekir.
        </Typography>
      ) : null}

      <ServiceEditorDrawer
        open={editing?.kind === "services"}
        onClose={() => setEditing(null)}
        service={editing?.kind === "services" ? editing.item : null}
        onSave={save}
        saving={creatingS || updatingS}
      />
      <PackageTemplateEditorDrawer
        open={editing?.kind === "packages"}
        onClose={() => setEditing(null)}
        template={editing?.kind === "packages" ? editing.item : null}
        services={services || []}
        onSave={save}
        saving={creatingT || updatingT}
      />
    </Box>
  );
}
